import React, { useState, useMemo } from 'react';
import Tooltip from '../components/common/Tooltip';
import { useMutation, useQuery, gql } from '@apollo/client';
import { GET_ALL_JOBS } from '../graphql/queries/jobs';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import PortfolioRequired from '../components/PortfolioRequired';
import { Link, useNavigate } from 'react-router-dom';
import { formatRelativeTime } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { DELETE_TXIMPORT_JOB } from '../graphql/mutations/tximport';
import EmptyState from '../components/EmptyState';
import { IMPORTS_RESULT_ROUTE_PREFIX, IMPORTS_REVIEW_ROUTE_PREFIX, STOCK_IMPORTS_RESULT_ROUTE_PREFIX, STOCK_IMPORTS_REVIEW_ROUTE_PREFIX } from '../constants/routes';
import { useImportModal } from '../contexts/ImportModalContext';

const DELETE_EXPORT_JOB = gql`
  mutation DeleteExportJob($jobId: ID!) {
    deleteExportJob(jobId: $jobId) {
      success
      error
    }
  }
`;

const importDetails = (job) => {
  const name = job.sourceName || '—';
  return job.importSource === 'degiro_transactions' ? `${name} · Stocks` : name;
};

const STATUS_STYLES = {
  QUEUED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  WAITING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  RUNNING: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  SUCCEEDED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  TIMEOUT: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  CANCELED: 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-200',
};

const JOB_TYPE_LABELS = {
  USER_TRANSACTIONS_CSV: 'Import',
  TRANSACTIONS_EXPORT: 'Export',
};

const JOB_TYPE_STYLES = {
  USER_TRANSACTIONS_CSV: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  TRANSACTIONS_EXPORT: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
};

const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString();
};

const formatFileSize = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const JobsTable = ({ jobs, onDeleteJob, onDownloadExport, reviewRoutePrefix = IMPORTS_REVIEW_ROUTE_PREFIX, resultRoutePrefix = IMPORTS_RESULT_ROUTE_PREFIX }) => {
  const navigate = useNavigate();

  if (!jobs.length) {
    return (
      <EmptyState
        title="No jobs yet"
        description="Start an import or export to see progress here."
      />
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="divide-y divide-gray-200 dark:divide-gray-700 md:hidden">
        {jobs.map((job) => {
          const isImport = job.type === 'USER_TRANSACTIONS_CSV';
          const isExport = job.type === 'TRANSACTIONS_EXPORT';
          const awaitingApproval = isImport && job.status === 'WAITING' && job.stage === 'REVIEWING';
          const canDownload = isExport && job.status === 'SUCCEEDED';
          const details = isImport ? importDetails(job) : (job.fileName || formatFileSize(job.fileSize));
          const statusBadge = (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[job.status] || 'bg-gray-100 text-gray-700'}`}>
              {job.status}
            </span>
          );
          const handleOpen = () => {
            if (isImport) navigate(`${resultRoutePrefix}/${job.id}`);
          };
          return (
            <div key={job.id} className="p-3">
              <button
                type="button"
                onClick={isImport ? handleOpen : undefined}
                className={`w-full text-left ${isImport ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${JOB_TYPE_STYLES[job.type] || 'bg-gray-100 text-gray-700'}`}>
                        {JOB_TYPE_LABELS[job.type] || job.type}
                      </span>
                      {statusBadge}
                    </div>
                    <div className="mt-2 truncate text-sm font-medium text-gray-900 dark:text-gray-100" title={details}>
                      {details}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formatRelativeTime(job.createdAt)} · {job.rowCount != null ? `${job.rowCount.toLocaleString()} rows` : 'Rows —'}{job.stage ? ` · ${job.stage}` : ''}
                    </div>
                  </div>
                  {isImport && (
                    <span className="shrink-0 text-xs text-blue-600 dark:text-blue-400">Open</span>
                  )}
                </div>
                {job.error && (
                  <div className="mt-2 line-clamp-2 text-xs text-red-600 dark:text-red-400">
                    {job.error}
                  </div>
                )}
              </button>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {awaitingApproval && (
                  <Link
                    to={`${reviewRoutePrefix}/${job.id}`}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300 dark:hover:bg-indigo-900/70 transition-colors"
                  >
                    Review
                  </Link>
                )}
                {canDownload && (
                  <button
                    type="button"
                    onClick={() => onDownloadExport?.(job)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-300 dark:hover:bg-green-900/70 transition-colors"
                  >
                    Download
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDeleteJob?.(job)}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto md:block">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-100 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Created</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Details</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Rows</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Stage</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Error</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {jobs.map((job) => {
            const isImport = job.type === 'USER_TRANSACTIONS_CSV';
            const isExport = job.type === 'TRANSACTIONS_EXPORT';
            const awaitingApproval = isImport && job.status === 'WAITING' && job.stage === 'REVIEWING';
            const canDownload = isExport && job.status === 'SUCCEEDED';
            const retryRelative = job.retryAt ? formatRelativeTime(job.retryAt) : null;
            const retryAbsolute = job.retryAt ? formatDateTime(job.retryAt) : null;
            const hasAttemptsTooltip = (job.attempts ?? 0) > 1;
            const retryText = job.retryAt
              ? (retryRelative && retryRelative !== '-' ? retryRelative : (retryAbsolute || '—'))
              : null;
            const tooltipText = [`Attempts: ${job.attempts}`]
              .concat(retryText ? [`Retry: ${retryText}`] : [])
              .join(' • ');
            const statusBadge = (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[job.status] || 'bg-gray-100 text-gray-700'}`}>
                {job.status}
              </span>
            );
            const handleRowNavigate = () => {
              if (isImport) {
                navigate(`${resultRoutePrefix}/${job.id}`);
              }
            };
            const details = isImport
              ? importDetails(job)
              : (job.fileName || formatFileSize(job.fileSize));
            return (
              <tr
                key={job.id}
                role={isImport ? "button" : undefined}
                tabIndex={isImport ? 0 : undefined}
                onClick={isImport ? handleRowNavigate : undefined}
                onKeyDown={isImport ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowNavigate();
                  }
                } : undefined}
                className={isImport ? "hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" : ""}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <div className="flex items-center gap-2">
                    <Tooltip
                      title={`Created: ${formatDateTime(job.createdAt)} <br/> Updated: ${formatDateTime(job.updatedAt)}`}
                      position="top"
                      trigger="mouseenter"
                      arrow={true}
                      distance={8}
                    >
                      <span className="font-medium cursor-help">{formatRelativeTime(job.createdAt)}</span>
                    </Tooltip>
                    {job.retryAt && (
                      <Tooltip
                        title={`Retry: ${retryRelative} <br/> Attempts: ${job.attempts ?? 1}`}
                        position="top"
                        trigger="mouseenter"
                        arrow={true}
                        distance={8}
                      >
                        <span className="cursor-help text-orange-500 dark:text-orange-400" title="">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${JOB_TYPE_STYLES[job.type] || 'bg-gray-100 text-gray-700'}`}>
                    {JOB_TYPE_LABELS[job.type] || job.type}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-[200px] truncate" title={details}>
                  {details}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                  {job.rowCount != null ? job.rowCount.toLocaleString() : '—'}
                </td>
                <td className="px-6 py-4">
                  {hasAttemptsTooltip ? (
                    <Tooltip title={tooltipText} position="top" trigger="mouseenter" arrow={true} distance={8}>
                      {statusBadge}
                    </Tooltip>
                  ) : (
                    statusBadge
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                  {job.stage || '—'}
                </td>
                <td className="px-6 py-4 text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap break-words">
                  {job.error || ''}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex gap-2">
                      {awaitingApproval && (
                        <Link
                          to={`${reviewRoutePrefix}/${job.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300 dark:hover:bg-indigo-900/70 transition-colors"
                        >
                          Review
                        </Link>
                      )}
                      {canDownload && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownloadExport?.(job);
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-300 dark:hover:bg-green-900/70 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteJob?.(job);
                      }}
                      className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                      title="Delete job"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const FILTER_TABS = [
  { id: 'all', label: 'All Jobs' },
  { id: 'import', label: 'Imports', type: 'USER_TRANSACTIONS_CSV' },
  { id: 'export', label: 'Exports', type: 'TRANSACTIONS_EXPORT' },
];

const ImportsPage = ({ assetKind = 'crypto' }) => {
  const isStock = assetKind === 'stock';
  const { currentPortfolio } = usePortfolioContext();
  const portfolioId = currentPortfolio?.id;
  const [activeTab, setActiveTab] = useState('all');
  const reviewRoutePrefix = isStock ? STOCK_IMPORTS_REVIEW_ROUTE_PREFIX : IMPORTS_REVIEW_ROUTE_PREFIX;
  const resultRoutePrefix = isStock ? STOCK_IMPORTS_RESULT_ROUTE_PREFIX : IMPORTS_RESULT_ROUTE_PREFIX;
  const { openImportModal } = useImportModal();

  const { data, loading, error, refetch } = useQuery(GET_ALL_JOBS, {
    variables: { portfolioId, limit: 50 },
    pollInterval: 20000,
    fetchPolicy: 'network-only',
    skip: !portfolioId,
  });

  const allJobs = useMemo(
    () => (data?.jobs ?? []).filter((job) => {
      const jobAssetKind = job.assetKind === 'stock' ? 'stock' : 'crypto';
      return isStock ? jobAssetKind === 'stock' : jobAssetKind !== 'stock';
    }),
    [data?.jobs, isStock],
  );
  
  const visibleTabs = useMemo(
    () => (isStock ? FILTER_TABS.filter((tab) => tab.id !== 'export') : FILTER_TABS),
    [isStock],
  );
  const filteredJobs = useMemo(() => {
    const tab = visibleTabs.find(t => t.id === activeTab);
    if (!tab || !tab.type) return allJobs;
    return allJobs.filter(job => job.type === tab.type);
  }, [allJobs, activeTab, visibleTabs]);

  const [jobToDelete, setJobToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteImportJob, { loading: deleteImportLoading }] = useMutation(DELETE_TXIMPORT_JOB);
  const [deleteExportJob, { loading: deleteExportLoading }] = useMutation(DELETE_EXPORT_JOB);

  const handleRequestDelete = (job) => {
    setJobToDelete(job);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!jobToDelete) return;
    try {
      const isExport = jobToDelete.type === 'TRANSACTIONS_EXPORT';
      if (isExport) {
        await deleteExportJob({ variables: { jobId: jobToDelete.id } });
      } else {
        await deleteImportJob({ variables: { jobId: jobToDelete.id } });
      }
      toast.success(`${isExport ? 'Export' : 'Import'} job deleted`);
      setShowDeleteModal(false);
      setJobToDelete(null);
      refetch();
    } catch (err) {
      console.error('Failed to delete job', err);
      toast.error('Failed to delete job. Please try again.');
    }
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setJobToDelete(null);
  };

  const handleDownloadExport = async (job) => {
    if (!job.result) {
      toast.error('Export result not available');
      return;
    }
    try {
      const result = JSON.parse(job.result);
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      } else {
        toast.error('Download URL not available');
      }
    } catch (err) {
      console.error('Failed to parse export result', err);
      toast.error('Failed to download export');
    }
  };

  if (!portfolioId) {
    return <PortfolioRequired title={isStock ? 'Stock Imports' : 'Background Tasks'} description={isStock ? 'Select a portfolio to manage stock import jobs.' : 'Select a portfolio to manage import and export jobs.'} />;
  }

  const deleteJobLoading = deleteImportLoading || deleteExportLoading;

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{isStock ? 'Stock Imports' : 'Background Tasks'}</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {isStock ? 'Stock import jobs. Stock imports stay separate from crypto imports.' : 'Import and export jobs. All jobs are automatically deleted after 30 days.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0">
          {isStock && (
            <button
              type="button"
              onClick={() => openImportModal({ portfolioId, assetKind: 'stock' })}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
            >
              Import transactions
            </button>
          )}
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tab.type 
              ? allJobs.filter(j => j.type === tab.type).length 
              : allJobs.length;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  isActive
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    isActive 
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400' 
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {loading && !data ? (
        <div className="py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorAlert message="Failed to load jobs. Please try again." />
      ) : (
        <>
          <JobsTable 
            jobs={filteredJobs} 
            onDeleteJob={handleRequestDelete} 
            onDownloadExport={handleDownloadExport}
            reviewRoutePrefix={reviewRoutePrefix}
            resultRoutePrefix={resultRoutePrefix}
          />
          <ConfirmModal
            isOpen={showDeleteModal}
            title="Delete job?"
            message={
              jobToDelete
                ? `Job "${jobToDelete.sourceName || jobToDelete.fileName || jobToDelete.id}" and all associated data will be permanently deleted. This cannot be undone.`
                : 'This job will be permanently deleted. This cannot be undone.'
            }
            confirmLabel="Yes, delete job"
            onConfirm={handleConfirmDelete}
            onCancel={handleDeleteModalClose}
            loading={deleteJobLoading}
          />
        </>
      )}
    </div>
  );
};

export default ImportsPage;

